import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import Header from './components/Header';
import Sidebar from './components/Sidebar';

import Dashboard from './components/Dashboard';
import Casos from './components/Casos';
import Facturacion from './components/Facturacion';
import Clientes from './components/Clientes';
import Abogados from './components/Abogados';
import Usuarios from './components/Usuarios';
import Roles from './components/Roles';
import BaseDeDatos from './components/BaseDeDatos';
import IAAsistente from './components/IAAsistente';
import LegalBotChat from './components/LegalBotChat';
import Perfil from './components/Perfil';
import AbogadoLocationLogin from './components/AbogadoLocationLogin';
import SeguimientoJudicial from './components/SeguimientoJudicial';
import AbogadoDashboard from './components/AbogadoDashboard';
import Contabilidad from './components/Contabilidad';
import EncuestasSatisfaccion from './components/EncuestasSatisfaccion';
import ChatEncuesta from './components/ChatEncuesta';
import Calendario from './components/Calendario';
import apiClient from './utils/apiClient';

const LegalPlusSystem = ({ user, onLogout }) => {

  const role = user?.rol || user?.role;
  const roleNormalized = (role || '').toString().toLowerCase();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [permisos, setPermisos] = useState(null);
  const [permisosLoaded, setPermisosLoaded] = useState(false);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleChat = () => setIsChatOpen(!isChatOpen);

  // Cargar permisos del rol del usuario logueado
  useEffect(() => {
    apiClient.get('/mi-sesion/permisos')
      .then(res => { setPermisos(res.data?.permisos); setPermisosLoaded(true); })
      .catch(() => { setPermisos(null); setPermisosLoaded(true); });
  }, [role]);

  const notifications = [
    { id: 1, text: 'Nueva notificacion de cliente.', read: false },
    { id: 2, text: 'Recordatorio: Audiencia de caso 12345.', read: false },
  ];

  // Helper: verificar si el modulo tiene permiso
  const puede = (modulo, accion = 'ver') => {
    if (!permisos?.modulos) return false;
    return !!permisos.modulos[modulo]?.[accion];
  };

  // Determinar si el usuario es readOnly en un modulo
  const esReadOnly = (modulo) => {
    if (!permisos?.modulos?.[modulo]) return true;
    const p = permisos.modulos[modulo];
    return p.ver && !p.editar && !p.crear && !p.eliminar;
  };

  // Determinar ruta home
  const getHome = () => {
    if (puede('dashboard')) return '/';
    if (puede('casos')) return '/casos';
    if (puede('facturacion')) return '/facturacion';
    if (puede('contabilidad')) return '/contabilidad';
    if (puede('seguimiento')) return '/seguimiento';
    if (puede('cuenta_cobro')) return '/abogados';
    return '/';
  };

  // Esperar a que los permisos carguen antes de renderizar
  if (!permisosLoaded) {
    return (
      <div className="h-screen flex items-center justify-center bg-brand-night">
        <div className="relative">
          <div className="w-10 h-10 rounded-full border-2 border-brand-indigo/20 border-t-brand-indigo animate-spin" />
          <div className="absolute inset-0 rounded-full bg-brand-indigo/15 blur-xl animate-halo-breathe" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-screen overflow-hidden bg-brand-night text-brand-silver transition-colors duration-300">

      <Sidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        userRole={role}
        user={user}
        permisos={permisos}
      />

      <div className={`flex flex-col flex-1 overflow-hidden transition-all duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-[74px]'}`}>
        <Header
          toggleSidebar={toggleSidebar}
          toggleChat={toggleChat}
          notifications={notifications}
          user={user}
          onLogout={onLogout}
        />

        <main className="flex-1 overflow-auto">
          <Routes>

            {/* Perfil siempre accesible */}
            <Route path="/perfil" element={<Perfil user={user} />} />

            {/* Dashboard */}
            {puede('dashboard') && (
              <Route path="/" element={
                (roleNormalized === 'abogado' || roleNormalized === 'abogado_seguimiento')
                  ? <AbogadoDashboard embedded loggedInUser={user?.nombre || user?.name} onLogout={onLogout} />
                  : <Dashboard user={user} />
              } />
            )}

            {/* Casos (no para abogados, ellos ven casos en su AbogadoDashboard) */}
            {puede('casos') && roleNormalized !== 'abogado' && roleNormalized !== 'abogado_seguimiento' && (
              <Route path="/casos" element={<Casos user={user} readOnly={esReadOnly('casos')} />} />
            )}

            {/* Seguimiento */}
            {puede('seguimiento') && (
              <Route path="/seguimiento" element={<SeguimientoJudicial readOnly={esReadOnly('seguimiento')} />} />
            )}

            {/* Facturacion */}
            {puede('facturacion') && (
              <Route path="/facturacion" element={<Facturacion />} />
            )}

            {/* Clientes */}
            {puede('clientes') && (
              <Route path="/clientes" element={<Clientes user={user} />} />
            )}

            {/* Usuarios */}
            {puede('usuarios') && (
              <>
                <Route path="/usuarios" element={<Usuarios />} />
                <Route path="/roles" element={<Roles />} />
              </>
            )}

            {/* Contabilidad */}
            {puede('contabilidad') && (
              <Route path="/contabilidad" element={<Contabilidad user={user} />} />
            )}

            {/* Cuenta de cobro */}
            {puede('cuenta_cobro') && (
              <Route path="/abogados" element={<Abogados />} />
            )}

            {/* Encuestas */}
            {puede('encuestas') && (
              <>
                <Route path="/encuestas" element={<EncuestasSatisfaccion user={user} />} />
                <Route path="/encuestas/chats" element={<ChatEncuesta />} />
                <Route path="/calendario" element={<Calendario />} />
              </>
            )}

            {/* Extras (siempre para admin/asistente, se controla por rol) */}
            {(puede('casos') || puede('dashboard')) && (
              <>
                <Route path="/ia-asistente" element={<IAAsistente />} />
                <Route path="/base-de-datos" element={<BaseDeDatos />} />
                <Route path="/abogado-login" element={<AbogadoLocationLogin abogadoNameProp={user?.nombre || user?.name} />} />
              </>
            )}

            {/* Fallback */}
            <Route path="*" element={<Navigate to={getHome()} />} />

          </Routes>

        </main>
      </div>

      <LegalBotChat isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />

    </div>
  );
};

export default LegalPlusSystem;
